import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  InputNumber,
  Button,
  Space,
  Select,
  Switch,
  Upload,
  Typography,
  message,
  Row,
  Col,
  Card
} from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
// import ReactQuill from 'react-quill';
import ReactQuill from 'react-quill-new'
import 'react-quill/dist/quill.snow.css'
import { uploadSingleFile, deleteFileByUrl } from '../../services/uploadService'
import { colorService, Color } from '../../services/colorService'
import productConfigService, {
  ProductConfig
} from '../../services/productConfigService'

const { Option } = Select
const { Text } = Typography

export interface CategoryOption {
  _id: string
  name: string
}

export interface ProductFormProps {
  onSubmit: (data: any) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
  categories: CategoryOption[]
  initialValues?: any
}

// Constants moved to API - see getProductConfig()

export const USER_OPTIONS = [
  { value: 'Male', label: 'Nam' },
  { value: 'Female', label: 'Nữ' },
  { value: 'Unisex', label: 'Cả nam và nữ' }
]

const ProductForm: React.FC<ProductFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting,
  categories,
  initialValues
}) => {
  const [form] = Form.useForm()
  const [imageList, setImageList] = useState<string[]>(
    initialValues?.images || []
  )
  const [colorList, setColorList] = useState<string[]>([])
  const [selectedColorIds, setSelectedColorIds] = useState<string[]>([])
  const [quillValue, setQuillValue] = useState(
    initialValues?.description_full || ''
  )
  const [colors, setColors] = useState<Color[]>([])
  const [colorsLoading, setColorsLoading] = useState(false)
  const [productConfig, setProductConfig] = useState<ProductConfig>({
    purposes: [],
    materials: [],
    sizes: [],
    brands: [],
    weaving: [],
    printing: [],
    finishing: [],
    care: []
  })
  const [configLoading, setConfigLoading] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set())
  const [variantThumbnails, setVariantThumbnails] = useState<{
    [key: number]: string
  }>({})
  const [variantImages, setVariantImages] = useState<{
    [key: number]: string[]
  }>({})
  const [uploadingVariantThumbnails, setUploadingVariantThumbnails] = useState<{
    [key: number]: string
  }>({})
  const [uploadingVariantImages, setUploadingVariantImages] = useState<{
    [key: number]: Set<string>
  }>({})

  useEffect(() => {
    form.setFieldsValue({ images: imageList })
  }, [imageList, form])

  useEffect(() => {
    if (initialValues?.colors) {
      const colorNames = initialValues.colors.map((color: any) =>
        typeof color === 'string' ? color : color.name
      )
      const colorIds = initialValues.colors.map((color: any) =>
        typeof color === 'string' ? color : color._id || color.id
      )
      setColorList(colorNames)
      setSelectedColorIds(colorIds)
      form.setFieldsValue({ colors: colorNames })
    }
  }, [initialValues?.colors, form])

  useEffect(() => {
    if (initialValues?.variants) {
      const thumbnailMap: { [key: number]: string } = {}
      const imagesMap: { [key: number]: string[] } = {}

      initialValues.variants.forEach((variant: any, index: number) => {
        if (variant.thumbnail_url) {
          thumbnailMap[index] = variant.thumbnail_url
        }
        if (variant.images) {
          imagesMap[index] = variant.images
        }
      })
      setVariantThumbnails(thumbnailMap)
      setVariantImages(imagesMap)

      // Set title_en values for variants
      const variantsWithTitleEn = initialValues.variants.map((variant: any) => {
        const titleEn =
          initialValues?.translations
            ?.find((t: any) => t.type === 'en')
            ?.variants?.find((v: any) => v.id === variant.id)?.title || ''
        return {
          ...variant,
          title_en: titleEn
        }
      })

      form.setFieldValue('variants', variantsWithTitleEn)
      // Force update individual variant fields
      variantsWithTitleEn.forEach((variant: any, index: number) => {
        if (variant.title_en) {
          form.setFieldValue(['variants', index, 'title_en'], variant.title_en)
        }
      })
    }
  }, [initialValues?.variants, initialValues?.translations, form])

  useEffect(() => {
    const fetchColors = async () => {
      try {
        setColorsLoading(true)
        const response = await colorService.getColors()
        setColors(response.data)
      } catch (error) {
        console.error('Error fetching colors:', error)
        message.error('Lỗi khi tải danh sách màu')
      } finally {
        setColorsLoading(false)
      }
    }

    const fetchProductConfig = async () => {
      try {
        setConfigLoading(true)
        const config = await productConfigService.getProductConfig()
        setProductConfig(config)
      } catch (error) {
        console.error('Error fetching product config:', error)
        message.error('Lỗi khi tải cấu hình sản phẩm')
      } finally {
        setConfigLoading(false)
      }
    }

    fetchColors()
    fetchProductConfig()
  }, [])

  const handleUpload = async ({ file, fileList }: any) => {
    try {
      if (file.status === 'uploading') {
        setUploadingFiles(prev => new Set(prev).add(file.uid))
        const response = await uploadSingleFile(file.originFileObj)
        if (response.status === 'success') {
          const updatedFileList = fileList.map((f: any) => {
            if (f.uid === file.uid) {
              return {
                ...f,
                url: response.data.file.url,
                thumbUrl: response.data.file.url,
                status: 'done'
              }
            }
            return f
          })
          const newImageList = updatedFileList
            .filter((f: any) => f.url)
            .map((f: any) => f.url)
          setImageList(prev => {
            const combined = [
              ...prev,
              ...newImageList.filter((url: string) => !prev.includes(url))
            ]
            return combined
          })
        }
        setUploadingFiles(prev => {
          const newSet = new Set(prev)
          newSet.delete(file.uid)
          return newSet
        })
      } else if (file.status === 'removed') {
        try {
          const response = await deleteFileByUrl(file.url)
          if (response.status === 'success') {
            const remainingFiles = fileList.filter(
              (f: any) => f.uid !== file.uid
            )
            setImageList(
              remainingFiles.filter((f: any) => f.url).map((f: any) => f.url)
            )
            message.success('File deleted successfully')
          } else {
            message.error('Failed to delete file')
          }
        } catch (error) {
          console.error('Delete failed:', error)
          message.error('Failed to delete file')
          setImageList(imageList.filter((url: string) => url !== file.url))
        }
      }
    } catch (error) {
      console.error('Upload failed:', error)
      message.error('Upload failed')
      setUploadingFiles(prev => {
        const newSet = new Set(prev)
        newSet.delete(file.uid)
        return newSet
      })
    }
  }

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  )

  const handleFinish = (values: any) => {
    // Xử lý dữ liệu trả về đúng format
    const {
      name,
      status,
      isVisible,
      isBuyOnline,
      price_regular,
      price_sale,
      sizes,
      material,
      purposes,
      user,
      materialType,
      description_full,
      tags,
      images,
      category,
      weaving,
      printing,
      finishing,
      care,
      creative_note
    } = values
    // Process variants to add IDs (using index as ID)
    const processedVariants = (values.variants || []).map(
      (variant: any, index: number) => ({
        ...variant,
        id: index
      })
    )

    const data: any = {
      name,
      status: status ?? 'available',
      isVisible: isVisible ?? true,
      isBuyOnline: isBuyOnline ?? true,
      price: price_regular
        ? { regular: price_regular, sale: price_sale || null }
        : undefined,
      sizes: sizes || [],
      colors: selectedColorIds?.map((colorId: string) => colorId) || [],
      variants: processedVariants,
      material,
      purposes: purposes || [],
      user,
      materialType,
      description: {
        short: '',
        full: description_full
      },
      tags: tags || [],
      images: images,
      category,
      weaving,
      printing,
      finishing,
      care,
      creative_note,
      translations: [
        {
          type: 'en',
          name: values.name_en || '',
          creative_note: values.creative_note_en || '',
          description: {
            short: '',
            full: values.description_full_en || ''
          },
          variants: (values.variants || []).map(
            (variant: any, index: number) => ({
              id: index,
              title: variant.title_en || '',
              description: variant.description || ''
            })
          )
        }
      ]
    }
    if (!data.sizes.length) delete data.sizes
    if (!data.price) delete data.price
    onSubmit(data)
  }

  return (
    <>
      <Form
        form={form}
        layout='vertical'
        initialValues={{
          status: 'available',
          isVisible: true,
          isBuyOnline: true,
          material: '',
          purposes: [],
          user: '',
          colors: initialValues?.colors
            ? initialValues.colors.map((color: any) =>
                typeof color === 'string' ? color : color.name
              )
            : [],
          variants:
            initialValues?.variants?.map((variant: any) => {
              const titleEn =
                initialValues?.translations
                  ?.find((t: any) => t.type === 'en')
                  ?.variants?.find((v: any) => v.id === variant.id)?.title || ''
              return {
                title: variant.title,
                title_en: titleEn,
                thumbnail_url: variant.thumbnail_url,
                images: variant.images,
                description: variant.description
              }
            }) || [],
          price_regular: initialValues?.price?.regular,
          price_sale: initialValues?.price?.sale,
          name_en:
            initialValues?.translations?.find((t: any) => t.type === 'en')
              ?.name || '',
          description_full_en:
            initialValues?.translations?.find((t: any) => t.type === 'en')
              ?.description?.full || '',
          creative_note_en:
            initialValues?.translations?.find((t: any) => t.type === 'en')
              ?.creative_note || '',
          ...initialValues,
          price: undefined
        }}
        onFinish={handleFinish}
      >
        <Form.Item
          name='name'
          label='Tên sản phẩm'
          rules={[{ required: true, message: 'Vui lòng nhập tên sản phẩm' }]}
        >
          <Input
            placeholder='Nhập tên sản phẩm'
            style={{
              width: '100%',
              height: 32,
              borderRadius: 4,
              fontSize: 16,
              padding: '6px 12px'
            }}
          />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '-10px' }}>
          <Form.Item
            name='name_en'
            label='Tên sản phẩm (tiếng Anh)'
            // rules={[
            //   {
            //     required: true,
            //     message: 'Vui lòng nhập tên sản phẩm tiếng Anh'
            //   }
            // ]}
            style={{ marginBottom: '16px' }}
          >
            <Input
              placeholder='Enter product name in English'
              style={{
                width: '100%',
                height: 32,
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
                backgroundColor: '#f5f5f5'
              }}
            />
          </Form.Item>
        </div>

        <Row gutter={16} className='mt-4'>
          <Col span={6}>
            <Form.Item
              name='price_regular'
              label='Giá bán (VNĐ)'
              rules={[{ required: true, message: 'Vui lòng nhập giá bán' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder='Nhập giá bán'
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name='price_sale' label='Giá khuyến mãi (VNĐ)'>
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder='Nhập giá khuyến mãi'
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name='status' label='Trạng thái sản phẩm'>
              <Select placeholder='Chọn trạng thái'>
                <Option value='available'>Còn hàng</Option>
                <Option value='out_of_stock'>Hết hàng</Option>
                <Option value='discontinued'>Ngừng bán</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name='isVisible'
                  label='Hiển thị trên website'
                  valuePropName='checked'
                >
                  <Switch checkedChildren='Hiện' unCheckedChildren='Ẩn' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name='isBuyOnline'
                  label='Mua Online'
                  valuePropName='checked'
                >
                  <Switch checkedChildren='Bật' unCheckedChildren='Tắt' />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row gutter={16} className='mt-6'>
          <Col span={8}>
            <Form.Item
              name='category'
              label='Danh mục'
              rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
            >
              <Select placeholder='Chọn danh mục'>
                {categories.map(cat => (
                  <Option key={cat._id} value={cat._id}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='material'
              label='Chất liệu'
              rules={[{ required: true, message: 'Vui lòng chọn chất liệu' }]}
            >
              <Select placeholder='Chọn chất liệu' loading={configLoading}>
                {productConfig.materials.map(mt => (
                  <Option key={mt.value} value={mt.value}>
                    {mt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='user'
              label='Đối tượng sử dụng'
              rules={[
                { required: true, message: 'Vui lòng chọn đối tượng sử dụng' }
              ]}
            >
              <Select placeholder='Chọn đối tượng sử dụng'>
                {USER_OPTIONS.map(user => (
                  <Option key={user.value} value={user.value}>
                    {user.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} className='mt-6'>
          <Col span={12}>
            <Form.Item name='materialType' label='Thương hiệu'>
              <Select placeholder='Chọn thương hiệu' loading={configLoading}>
                {productConfig.brands.map(brand => (
                  <Option key={brand.value} value={brand.value}>
                    {brand.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name='weaving' label='Kỹ thuật dệt'>
              <Select placeholder='Chọn kỹ thuật dệt' loading={configLoading}>
                {productConfig.weaving.map(item => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name='purposes' label='Mục đích sử dụng' className='mt-6'>
          <Select
            mode='multiple'
            placeholder='Chọn mục đích sử dụng'
            loading={configLoading}
          >
            {productConfig.purposes.map(purpose => (
              <Option key={purpose.value} value={purpose.value}>
                {purpose.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name='sizes' label='Kích thước' className='mt-6'>
          <Select
            mode='multiple'
            placeholder='Chọn kích thước'
            loading={configLoading}
            optionLabelProp='label'
          >
            {productConfig.sizes.map(size => (
              <Option key={size.value} value={size.value} label={size.label}>
                {size.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name='colors' label='Màu sắc' className='mt-6'>
          <Select
            mode='multiple'
            placeholder='Chọn màu sắc'
            loading={colorsLoading}
            value={colorList}
            onChange={values => {
              setColorList(values)
              // Map color names to their corresponding IDs
              const selectedIds = values.map((colorName: string) => {
                const color = colors.find(c => c.name === colorName)
                return color ? color._id : colorName
              })
              setSelectedColorIds(selectedIds)
            }}
          >
            {colors.map(color => (
              <Option key={color._id} value={color.name}>
                <div className='flex items-center space-x-2'>
                  {color.hex ? (
                    <div
                      className='w-4 h-4 rounded border'
                      style={{ backgroundColor: color.hex }}
                    />
                  ) : color.image_url ? (
                    <img
                      src={color.image_url}
                      alt={color.name}
                      className='object-cover w-4 h-4 rounded border'
                    />
                  ) : (
                    <div className='w-4 h-4 bg-gray-200 rounded border' />
                  )}
                  <span>{color.name}</span>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name='printing' label='Công nghệ in'>
          <Select placeholder='Chọn công nghệ in' loading={configLoading}>
            {productConfig.printing.map(item => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name='finishing' label='Hoàn thiện'>
          <Select placeholder='Chọn hoàn thiện' loading={configLoading}>
            {productConfig.finishing.map(item => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name='care' label='Bảo quản'>
          <Select placeholder='Chọn hướng dẫn bảo quản' loading={configLoading}>
            {productConfig.care.map(item => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name='description_full'
          label='Mô tả chi tiết'
          rules={[{ required: true, message: 'Vui lòng nhập mô tả chi tiết' }]}
        >
          <ReactQuill
            theme='snow'
            value={quillValue}
            onChange={val => {
              setQuillValue(val)
              form.setFieldsValue({ description_full: val })
            }}
            placeholder='Mô tả chi tiết về sản phẩm'
            style={{
              borderRadius: '10px',
              height: '200px',
              marginBottom: '40px'
            }}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [
                  { list: 'ordered' },
                  { list: 'bullet' },
                  { indent: '-1' },
                  { indent: '+1' }
                ],
                ['link', 'image'],
                ['clean']
              ]
            }}
            formats={[
              'header',
              'bold',
              'italic',
              'underline',
              'strike',
              'blockquote',
              'list',
              'bullet',
              'indent',
              'link',
              'image'
            ]}
          />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '-20px' }}>
          <Form.Item
            name='description_full_en'
            label='Mô tả chi tiết (tiếng Anh)'
            // rules={[
            //   {
            //     required: true,
            //     message: 'Vui lòng nhập mô tả chi tiết tiếng Anh'
            //   }
            // ]}
            style={{ marginBottom: '40px' }}
          >
            <ReactQuill
              theme='snow'
              placeholder='Enter detailed product description in English'
              style={{
                borderRadius: '10px',
                height: '200px',
                marginBottom: '40px',
                backgroundColor: '#f5f5f5'
              }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [
                    { list: 'ordered' },
                    { list: 'bullet' },
                    { indent: '-1' },
                    { indent: '+1' }
                  ],
                  ['link', 'image'],
                  ['clean']
                ]
              }}
              formats={[
                'header',
                'bold',
                'italic',
                'underline',
                'strike',
                'blockquote',
                'list',
                'bullet',
                'indent',
                'link',
                'image'
              ]}
            />
          </Form.Item>
        </div>

        <Form.Item name='creative_note' label='Ghi chú thêm'>
          <Input.TextArea rows={2} placeholder='Nhập ghi chú thêm' />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '-10px' }}>
          <Form.Item
            name='creative_note_en'
            label='Ghi chú thêm (tiếng Anh)'
            style={{ marginBottom: '16px' }}
          >
            <Input.TextArea
              rows={2}
              placeholder='Enter additional notes in English'
              style={{
                backgroundColor: '#f5f5f5'
              }}
            />
          </Form.Item>
        </div>

        <Form.Item name='tags' label='Từ khóa (tags, không bắt buộc)'>
          <Select mode='tags' placeholder='Nhập từ khóa, nhấn Enter để thêm' />
        </Form.Item>

        <Form.List name='variants'>
          {(fields, { add, remove }) => (
            <div>
              <Text strong>Biến thể:</Text>
              {fields.map(({ key, name, ...restField }, index) => {
                const thumbnailUrl = variantThumbnails[name]
                const imagesList = variantImages[name] || []
                const uploadingThumbnail = uploadingVariantThumbnails[name]
                const uploadingImages =
                  uploadingVariantImages[name] || new Set()

                return (
                  <Card key={key} size='small' style={{ marginBottom: 16 }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'title']}
                      label='Tiêu đề'
                      rules={[
                        { required: true, message: 'Nhập tiêu đề' },
                        {
                          validator: (_, value) => {
                            const allTitles =
                              form
                                .getFieldValue('variants')
                                ?.map((v: any) => v?.title)
                                .filter(Boolean) || []
                            const duplicateCount = allTitles.filter(
                              (title: string) => title === value
                            ).length
                            if (duplicateCount > 1) {
                              return Promise.reject(
                                new Error('Tiêu đề phải unique')
                              )
                            }
                            return Promise.resolve()
                          }
                        }
                      ]}
                    >
                      <Input
                        placeholder='VD: Biến thể 1'
                        style={{
                          width: '100%',
                          height: 32,
                          borderRadius: 4,
                          fontSize: 16,
                          padding: '6px 12px'
                        }}
                      />
                    </Form.Item>

                    <div style={{ marginLeft: '20px', marginTop: '-10px' }}>
                      <Form.Item
                        label='Tiêu đề (tiếng Anh)'
                        style={{ marginBottom: '16px' }}
                        // rules={[
                        //   {
                        //     required: true,
                        //     message: 'Vui lòng nhập tiêu đề tiếng Anh'
                        //   }
                        // ]}
                      >
                        <Input
                          placeholder='Enter variant title in English'
                          value={
                            form.getFieldValue([
                              'variants',
                              name,
                              'title_en'
                            ]) || ''
                          }
                          onChange={e => {
                            const currentVariants =
                              form.getFieldValue('variants') || []
                            const updatedVariants = [...currentVariants]
                            updatedVariants[name] = {
                              ...updatedVariants[name],
                              title_en: e.target.value
                            }
                            form.setFieldValue('variants', updatedVariants)
                          }}
                          style={{
                            width: '100%',
                            height: 32,
                            borderRadius: 4,
                            fontSize: 16,
                            padding: '6px 12px',
                            backgroundColor: '#f5f5f5'
                          }}
                        />
                      </Form.Item>
                    </div>

                    <Form.Item
                      {...restField}
                      name={[name, 'thumbnail_url']}
                      label='Ảnh thumbnail'
                      rules={[
                        { required: true, message: 'Upload ảnh thumbnail' }
                      ]}
                    >
                      <Upload
                        listType='picture-card'
                        maxCount={1}
                        fileList={[
                          ...(thumbnailUrl
                            ? [
                                {
                                  uid: '-1',
                                  name: 'thumbnail',
                                  status: 'done' as 'done',
                                  url: thumbnailUrl,
                                  thumbUrl: thumbnailUrl
                                }
                              ]
                            : []),
                          ...(uploadingThumbnail
                            ? [
                                {
                                  uid: uploadingThumbnail,
                                  name: 'Uploading...',
                                  status: 'uploading' as 'uploading',
                                  url: '',
                                  thumbUrl: ''
                                }
                              ]
                            : [])
                        ]}
                        customRequest={({ file, onSuccess }: any) => {
                          setTimeout(() => {
                            onSuccess('ok')
                          }, 0)
                        }}
                        onChange={async ({ file, fileList }) => {
                          if (
                            file &&
                            file.status === 'uploading' &&
                            file.originFileObj
                          ) {
                            setUploadingVariantThumbnails(prev => ({
                              ...prev,
                              [name]: file.uid
                            }))
                            try {
                              const response = await uploadSingleFile(
                                file.originFileObj
                              )
                              if (response.status === 'success') {
                                form.setFieldValue(
                                  ['variants', name, 'thumbnail_url'],
                                  response.data.file.url
                                )
                                setVariantThumbnails(prev => ({
                                  ...prev,
                                  [name]: response.data.file.url
                                }))
                                message.success('Upload thumbnail thành công')
                              }
                            } catch (error) {
                              console.error('Upload failed:', error)
                              message.error('Upload thumbnail thất bại')
                            } finally {
                              setUploadingVariantThumbnails(prev => {
                                const newState = { ...prev }
                                delete newState[name]
                                return newState
                              })
                            }
                          } else if (file.status === 'removed') {
                            try {
                              if (file.url) {
                                const response = await deleteFileByUrl(file.url)
                                if (response.status === 'success') {
                                  form.setFieldValue(
                                    ['variants', name, 'thumbnail_url'],
                                    ''
                                  )
                                  setVariantThumbnails(prev => {
                                    const newState = { ...prev }
                                    delete newState[name]
                                    return newState
                                  })
                                  message.success('Xóa thumbnail thành công')
                                } else {
                                  message.error('Xóa thumbnail thất bại')
                                }
                              }
                            } catch (error) {
                              console.error('Delete failed:', error)
                              message.error('Xóa thumbnail thất bại')
                            }
                          }
                        }}
                      >
                        {!thumbnailUrl && !uploadingThumbnail && (
                          <PlusOutlined />
                        )}
                      </Upload>
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, 'images']}
                      label='Ảnh chi tiết'
                      rules={[
                        { required: true, message: 'Upload ít nhất 1 ảnh' }
                      ]}
                      getValueFromEvent={e => {
                        if (Array.isArray(e)) {
                          return e
                        }
                        return imagesList
                      }}
                    >
                      <Upload
                        listType='picture-card'
                        fileList={[
                          ...imagesList.map((url: string, index: number) => ({
                            uid: `-${index}`,
                            name: url?.split('/').pop() || `image-${index}`,
                            status: 'done' as 'done',
                            url: url,
                            thumbUrl: url
                          })),
                          ...Array.from(uploadingImages).map(uid => ({
                            uid,
                            name: 'Uploading...',
                            status: 'uploading' as 'uploading',
                            url: '',
                            thumbUrl: ''
                          }))
                        ]}
                        customRequest={({ file, onSuccess }: any) => {
                          setTimeout(() => {
                            onSuccess('ok')
                          }, 0)
                        }}
                        onChange={async ({ file, fileList }) => {
                          try {
                            if (file.status === 'uploading') {
                              setUploadingVariantImages(prev => ({
                                ...prev,
                                [name]: new Set(prev[name] || []).add(file.uid)
                              }))
                              const response = await uploadSingleFile(
                                file.originFileObj!
                              )
                              if (response.status === 'success') {
                                const updatedFileList = fileList.map(
                                  (f: any) => {
                                    if (f.uid === file.uid) {
                                      return {
                                        ...f,
                                        url: response.data.file.url,
                                        thumbUrl: response.data.file.url,
                                        status: 'done'
                                      }
                                    }
                                    return f
                                  }
                                )
                                const newImageList = updatedFileList
                                  .filter((f: any) => f.url)
                                  .map((f: any) => f.url)
                                setVariantImages(prev => {
                                  const combined = [
                                    ...(prev[name] || []),
                                    ...newImageList.filter(
                                      (url: string) =>
                                        !(prev[name] || []).includes(url)
                                    )
                                  ]
                                  form.setFieldValue(
                                    ['variants', name, 'images'],
                                    combined
                                  )
                                  return {
                                    ...prev,
                                    [name]: combined
                                  }
                                })
                              }
                              setUploadingVariantImages(prev => {
                                const newState = { ...prev }
                                if (newState[name]) {
                                  newState[name].delete(file.uid)
                                  if (newState[name].size === 0) {
                                    delete newState[name]
                                  }
                                }
                                return newState
                              })
                            } else if (file.status === 'removed') {
                              try {
                                if (file.url) {
                                  const response = await deleteFileByUrl(
                                    file.url
                                  )
                                  if (response.status === 'success') {
                                    const remainingFiles = fileList.filter(
                                      (f: any) => f.uid !== file.uid
                                    )
                                    const remainingImages = remainingFiles
                                      .filter((f: any) => f.url)
                                      .map((f: any) => f.url)
                                    setVariantImages(prev => ({
                                      ...prev,
                                      [name]: remainingImages
                                    }))
                                    form.setFieldValue(
                                      ['variants', name, 'images'],
                                      remainingImages
                                    )
                                    message.success('Xóa ảnh thành công')
                                  } else {
                                    message.error('Xóa ảnh thất bại')
                                  }
                                }
                              } catch (error) {
                                console.error('Delete failed:', error)
                                message.error('Xóa ảnh thất bại')
                              }
                            }
                          } catch (error) {
                            console.error('Upload failed:', error)
                            message.error('Upload ảnh thất bại')
                            setUploadingVariantImages(prev => {
                              const newState = { ...prev }
                              if (newState[name]) {
                                newState[name].delete(file.uid)
                                if (newState[name].size === 0) {
                                  delete newState[name]
                                }
                              }
                              return newState
                            })
                          }
                        }}
                        multiple
                      >
                        {imagesList.length + uploadingImages.size >= 8
                          ? null
                          : uploadButton}
                      </Upload>
                    </Form.Item>

                    <div className='flex justify-end'>
                      <Button
                        type='text'
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      >
                        Xóa
                      </Button>
                    </div>
                  </Card>
                )
              })}
              <Form.Item>
                <Button
                  type='dashed'
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Thêm biến thể
                </Button>
              </Form.Item>
            </div>
          )}
        </Form.List>

        <Form.Item
          label='Images'
          name='images'
          rules={[
            { required: true, message: 'Please upload at least one image' }
          ]}
          getValueFromEvent={e => {
            if (Array.isArray(e)) {
              return e
            }
            return imageList
          }}
        >
          <Upload
            listType='picture-card'
            fileList={[
              ...imageList
                .filter(url => url)
                .map((url: string, index: number) => {
                  return {
                    uid: `-${index}`,
                    name: url?.split('/').pop() || `image-${index}`,
                    status: 'done' as 'done',
                    url: url,
                    thumbUrl: url
                  }
                }),
              ...Array.from(uploadingFiles).map(uid => ({
                uid,
                name: 'Uploading...',
                status: 'uploading' as 'uploading',
                url: '',
                thumbUrl: ''
              }))
            ]}
            customRequest={({ file, onSuccess }: any) => {
              setTimeout(() => {
                onSuccess('ok')
              }, 0)
            }}
            onChange={handleUpload}
            multiple
          >
            {imageList.length + uploadingFiles.size >= 8 ? null : uploadButton}
          </Upload>
        </Form.Item>

        <Form.Item style={{ marginTop: 24 }}>
          <Space>
            <Button type='primary' htmlType='submit' loading={isSubmitting}>
              Lưu sản phẩm
            </Button>
            <Button onClick={onCancel}>Hủy</Button>
          </Space>
        </Form.Item>
      </Form>
    </>
  )
}

export default ProductForm
